import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import UploadIcon from "@mui/icons-material/Upload"
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Button,
	CircularProgress,
	Grid,
	Paper,
	Snackbar,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material"
import { green, red } from "@mui/material/colors"
import { styled } from "@mui/material/styles"
import { Box } from "@mui/system"
import { AxiosError, AxiosResponse } from "axios"
import { MuiColorInput, MuiColorInputValue } from "mui-color-input"
import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { ReactComponent as SettingSvg } from "../../../assets/img/settings.svg"
import { AppDispatch } from "../../../store"
import {
	BankAccountState,
	updateBankAccount,
} from "../../../store/actions/bankAccounts"
import {
	CorporatesState,
	updateCorporate,
} from "../../../store/actions/corporates"

interface ICreateFormProps {
	isEditMode?: boolean
	tabIndex?: number
	corporate?: CorporatesState
}

interface TabPanelProps {
	children?: React.ReactNode
	index: number
	value: number
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	)
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	}
}

const CreateForm = ({ isEditMode, corporate, tabIndex }: ICreateFormProps) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const [alertMessage, setAlertMessage] = useState<string | undefined>(
		undefined
	)
	const [isErrorSnackBarOpen, setIsErrorSnackBarOpen] = useState<boolean>(false)
	const [isSavingForm, setIsSavingForm] = useState<boolean>(false)
	const [isSaved, setIsSaved] = useState<boolean>(false)
	const [formData, setFormData] = useState<CorporatesState>({
		autoAccept: undefined,
		bank: undefined,
		campaigns: [],
		checkIban: undefined,
		contiCorrenti: [],
		corporateSenderId: undefined,
		corporateSenderName: undefined,
		createdBy: undefined,
		creationDate: undefined,
		daysToFirstReminder: undefined,
		daysToSecondReminder: undefined,
		deleted: undefined,
		enabled: undefined,
		fileVersion: undefined,
		hasBankAuthorizer: undefined,
		id: undefined,
		logo: undefined,
		modificationDate: undefined,
		modifiedBy: undefined,
		plickOpen: undefined,
		primaryColor: undefined,
		secondaryColor: undefined,
		timeToExpiry: undefined,
		version: undefined,
		voucher: undefined,
		whatsapp: undefined,
	})
	const [tabSelected, setTabSelected] = useState<number>(0)
	const [updateFormData, setUpdateFormData] = useState<
		CorporatesState | undefined
	>(undefined)
	const [errors, setErrors] = useState(undefined)
	const [corporateImgSrc, setCorporateImgSrc] = useState(undefined)

	const VisuallyHiddenInput = styled("input")({
		clip: "rect(0 0 0 0)",
		clipPath: "inset(50%)",
		height: 1,
		overflow: "hidden",
		position: "absolute",
		bottom: 0,
		left: 0,
		whiteSpace: "nowrap",
		width: 1,
	})

	useEffect(() => {
		if (corporate?.contiCorrenti) setTabSelected(tabIndex ?? 0)
	}, [tabIndex])

	useEffect(() => {
		if (isEditMode) {
			setFormData({
				autoAccept: corporate?.autoAccept,
				bank: corporate?.bank,
				campaigns: corporate?.campaigns,
				checkIban: corporate?.checkIban,
				contiCorrenti: corporate?.contiCorrenti,
				corporateSenderId: corporate?.corporateSenderId,
				corporateSenderName: corporate?.corporateSenderName,
				createdBy: corporate?.createdBy,
				creationDate: corporate?.creationDate,
				daysToFirstReminder: corporate?.daysToFirstReminder,
				daysToSecondReminder: corporate?.daysToSecondReminder,
				deleted: corporate?.deleted,
				enabled: corporate?.enabled,
				fileVersion: corporate?.fileVersion,
				hasBankAuthorizer: corporate?.hasBankAuthorizer,
				id: corporate?.id,
				logo: corporate?.logo,
				modificationDate: corporate?.modificationDate,
				modifiedBy: corporate?.modifiedBy,
				plickOpen: corporate?.plickOpen,
				primaryColor: corporate?.primaryColor ?? "#38719d",
				secondaryColor: corporate?.secondaryColor ?? "#b62377",
				timeToExpiry: corporate?.timeToExpiry,
				version: corporate?.version,
				voucher: corporate?.voucher,
				whatsapp: corporate?.whatsapp,
			})
		}
	}, [corporate?.corporateSenderId, isEditMode])

	useEffect(() => {
		if (formData?.logo) {
			const base64Image = formData.logo
			if (formData.logo instanceof File) {
				return null
			}
			const newcorporateImgSrc = base64Image && atob(base64Image)
			setCorporateImgSrc(newcorporateImgSrc)
		}
	}, [formData?.logo])

	const handleChangeTabSelected = (event: SyntheticEvent, newValue: number) => {
		setTabSelected(newValue)
	}

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
		if (isEditMode) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}
		setErrors((prevData) => ({
			...prevData,
			[name]: undefined,
		}))
	}

	const handleInputContiCorrentiChange = (
		event: ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const { name, value } = event.target
		const idConto = Number(event.target.dataset.idconto)
		// Aggiorna formData per riflettere i cambiamenti globali
		setFormData((prevData) => {
			const newContiCorrenti = [...prevData?.contiCorrenti] ?? []
			newContiCorrenti[index] = {
				...newContiCorrenti[index],
				id: idConto,
				[name]: value,
			}
			return {
				...prevData,
				["contiCorrenti"]: newContiCorrenti,
			}
		})

		// Se in modalità di modifica (edit), aggiorna anche updateFormData
		if (isEditMode) {
			setUpdateFormData((prevData) => {
				const newContiCorrenti =
					prevData && prevData?.contiCorrenti
						? [...prevData?.contiCorrenti]
						: []
				newContiCorrenti[index] = {
					...newContiCorrenti[index],
					id: idConto,
					[name]: value,
				}
				return {
					...prevData,
					["contiCorrenti"]: newContiCorrenti,
				}
			})
		}
	}
	const handleColorChange = (value: string, name: string) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
		if (isEditMode) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}
	}

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, files } = event.target
		if (files?.[0]) {
			if (isEditMode) {
				setUpdateFormData((prevData) => ({
					...prevData,
					[name]: files[0],
				}))
			}

			const reader = new FileReader()
			reader.onloadend = () => {
				setCorporateImgSrc(reader.result)
				setFormData((prevData) => ({
					...prevData,
					[name]: btoa(reader.result),
				}))
			}
			reader.readAsDataURL(files[0])
		}
	}

	const handleSaveForm = () => {
		setIsSavingForm(true)

		if (
			updateFormData?.contiCorrenti &&
			updateFormData?.contiCorrenti.length > 0
		) {
			updateFormData?.contiCorrenti.map((conto: BankAccountState) =>
				dispatch(
					updateBankAccount({
						bankAccountData: conto,
						idBank: corporate?.bank,
						contoId: conto?.id,
					})
				)
			)
		}

		dispatch(
			updateCorporate({
				corporateData: updateFormData,
				idBank: corporate?.bank,
				idCorporate: corporate?.id,
			})
		)
			.then((result: AxiosResponse) => {
				setIsSavingForm(false)
				if (result?.meta?.requestStatus === "rejected") {
					if (result?.payload?.status === 403) {
						setAlertMessage(
							t("unauthorized.error.alertMessage", { ns: "common" })
						)
						setIsErrorSnackBarOpen(true)
					} else if (result?.payload?.status === 400) {
						const errorSplit = result?.payload?.data?.message.split(".")

						const backendErrors = result.payload?.data?.errors?.reduce(
							(acc, err) => {
								acc[err.field] = err.defaultMessage
								return acc
							},
							{}
						)

						setErrors((prevData) => ({
							...prevData,
							...backendErrors,
							[errorSplit[1]]: result?.payload?.data?.message,
						}))
					}
				} else {
					setIsSaved(true)
				}
			})
			.catch((error: AxiosError) => {
				if (error.response && error.response.status === 400) {
					const backendErrors = error.response.data.reduce((acc, err) => {
						acc[err.field] = err.defaultMessage
						return acc
					}, {})
					setErrors(backendErrors)
				} else if (error.response && error.response.status === 404) {
					setAlertMessage(
						t("unauthorized.error.alertMessage", { ns: "common" })
					)
					setIsErrorSnackBarOpen(true)
				}
			})
	}

	const campaigns = corporate?.campaigns || []

	const campaignNames =
		campaigns?.map((c) => c.campaignName).join(", ") || "N/A"

	const conti = corporate?.contiCorrenti || []

	const contiNames = conti?.map((c) => c.contoCorrente).join(", ") || "N/A"

	return (
		<>
			{isSaved ? (
				<Paper square elevation={0} sx={{ p: 4 }}>
					<Grid container>
						<Grid item>
							<SettingSvg width={"200px"} height={"200px"} />
						</Grid>
						<Grid item sx={{ alignContent: "center" }}>
							<Typography sx={{ fontWeight: "bold" }} component="div">
								{t(isEditMode ? "form.modified" : "form.created", {
									ns: "corporate",
									corporateName: formData?.corporateSenderName,
								})}
							</Typography>
						</Grid>
					</Grid>
				</Paper>
			) : (
				<Grid
					container
					flexDirection={"column"}
					justifyContent={"space-between"}
				>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							value={tabSelected}
							onChange={handleChangeTabSelected}
							aria-label="Tab per sezioni"
						>
							<Tab
								label={t("tabs.general", { ns: "corporate" })}
								{...a11yProps(0)}
							/>
							<Tab
								label={t("tabs.accounts", { ns: "corporate" })}
								{...a11yProps(1)}
							/>
						</Tabs>
					</Box>
					<CustomTabPanel value={tabSelected} index={0}>
						<Grid container gap={"16px"} flexDirection={"column"}>
							<Grid item sx={{ paddingBottom: "34px" }}>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									{t("bank", { ns: "corporate" })}: {formData?.bank}
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									<TextField
										fullWidth
										InputLabelProps={{
											shrink: Boolean(
												updateFormData?.corporateSenderName ||
													formData?.corporateSenderName
											),
										}}
										error={!!errors?.corporateSenderName}
										helperText={t(errors?.corporateSenderName, {
											ns: "corporate",
										})}
										id="corporateSenderName"
										label={t("form.corporateSenderName", { ns: "corporate" })}
										name="corporateSenderName"
										value={
											updateFormData?.corporateSenderName ||
											formData?.corporateSenderName
										}
										onChange={handleInputChange}
										required
									/>
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									<img
										alt={t("logo.corporate", { ns: "common" })}
										style={{
											maxWidth: "50px",
											height: "fit-content",
											verticalAlign: "middle",
										}}
										src={corporateImgSrc}
									/>
									<Button
										component="label"
										role={undefined}
										variant="contained"
										tabIndex={-1}
										startIcon={<UploadIcon />}
									>
										{t("form.changeLogo", { ns: "corporate" })}
										<VisuallyHiddenInput
											helperText={t(errors?.corporateImage, {
												ns: "corporate",
											})}
											name="corporateImage"
											type="file"
											onChange={(event) => handleFileChange(event)}
										/>
									</Button>
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									<MuiColorInput
										label={"Colore primario"}
										format="hex"
										value={formData?.primaryColor as MuiColorInputValue}
										onChange={(newValue) =>
											handleColorChange(newValue, "primaryColor")
										}
									/>
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									<MuiColorInput
										label={"Colore secondario"}
										format="hex"
										value={formData?.secondaryColor as MuiColorInputValue}
										onChange={(newValue) =>
											handleColorChange(newValue, "secondaryColor")
										}
									/>
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									{t("table.enabled", { ns: "corporate" })}:{" "}
									{corporate?.enabled ? (
										<CheckIcon sx={{ color: green[500] }} />
									) : (
										<CloseIcon sx={{ color: red[500] }} />
									)}
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									id: {corporate?.corporateSenderId}
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									{t("card.campaign", {
										ns: "corporate",
										postProcess: "interval",
										count: corporate?.campaigns?.length ?? 0,
									})}
									: {campaignNames}
								</Grid>
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									{t("card.account", {
										ns: "corporate",
										postProcess: "interval",
										count: corporate?.campaigns?.length ?? 0,
									})}
									: {contiNames}
								</Grid>

								{/* 
								<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
									<TextField
										fullWidth
										InputLabelProps={{ shrink: true }}
										error={!!errors?.timeToExpiry}
										helperText={t(errors?.timeToExpiry, { ns: "corporate" })}
										onChange={handleInputChange}
										id="timeToExpiry"
										name="timeToExpiry"
										label={t("form.timeExpire", { ns: "corporate" })}
										value={
											updateFormData?.timeToExpiry ??
											formData?.timeToExpiry ??
											""
										}
										type="number"
									/>
								</Grid>
				
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-auto-accept">
											{t("form.autoAccept", { ns: "corporate" })}
										</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-auto-accept"
											name="row-radio-auto-accept-group"
										>
											<FormControlLabel
												value={formData?.autoAccept}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.autoAccept}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-check-iban">
											{t("form.checkIban", { ns: "corporate" })}
										</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-check-iban"
											name="row-radio-check-iban-group"
										>
											<FormControlLabel
												value={formData?.checkIban}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.checkIban}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-daysToFirstReminder">
											{t("form.daysToFirstReminder", { ns: "corporate" })}
										</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-daysToFirstReminder"
											name="row-radio-daysToFirstReminder-group"
										>
											<FormControlLabel
												value={formData?.daysToFirstReminder}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.daysToFirstReminder}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-daysToSecondReminder">
											{t("form.daysToSecondReminder", { ns: "corporate" })}
										</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-daysToSecondReminder"
											name="row-radio-daysToSecondReminder-group"
										>
											<FormControlLabel
												value={formData?.daysToSecondReminder}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.daysToSecondReminder}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-fileVersion">
											{t("form.fileVersion", { ns: "corporate" })}
										</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-fileVersion"
											name="row-radio-fileVersion-group"
										>
											<FormControlLabel
												value={formData?.fileVersion}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.fileVersion}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-timeToExpiry">
											{t("form.timeToExpiry", { ns: "corporate" })}
										</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-timeToExpiry"
											name="row-radio-timeToExpiry-group"
										>
											<FormControlLabel
												value={formData?.timeToExpiry}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.timeToExpiry}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-voucher">
											{t("form.voucher", { ns: "corporate" })}
										</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-voucher"
											name="row-radio-voucher-group"
										>
											<FormControlLabel
												value={formData?.voucher}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.voucher}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item flexGrow={1}>
									<FormControl>
										<FormLabel id="radio-whatsapp">Whatsapp</FormLabel>
										<RadioGroup
											row
											aria-labelledby="radio-whatsapp"
											name="row-radio-whatsapp-group"
										>
											<FormControlLabel
												value={formData?.whatsapp}
												control={<Radio />}
												label="Attivo"
											/>
											<FormControlLabel
												value={!formData?.whatsapp}
												control={<Radio />}
												label="Disattivo"
											/>
										</RadioGroup>
									</FormControl>
								</Grid>
								*/}
							</Grid>
						</Grid>
					</CustomTabPanel>
					<CustomTabPanel value={tabSelected} index={1}>
						{corporate?.contiCorrenti.map((cc, index) => {
							return (
								<Accordion key={cc.contoCorrente + cc.senderAccount}>
									<AccordionSummary
										expandIcon={<ArrowDropDownIcon />}
										aria-controls="panel2-content"
										id="panel2-header"
									>
										<Typography>{cc.contoCorrente}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Grid container gap={"16px"} flexDirection={"column"}>
											<Grid item flexGrow={1}>
												<TextField
													fullWidth
													InputLabelProps={{ shrink: true }}
													// error={!!errors?.timeToExpiry}
													/*helperText={t(errors?.timeToExpiry, {
														ns: "corporate",
													})}*/
													onChange={(event: ChangeEvent<HTMLInputElement>) =>
														handleInputContiCorrentiChange(event, index)
													}
													inputProps={{
														"data-idconto":
															formData?.contiCorrenti?.[index]?.id,
													}}
													id="contoCorrente"
													name="contoCorrente"
													label={t("form.bankAccountName", { ns: "corporate" })}
													value={
														updateFormData?.contiCorrenti?.[index]
															?.contoCorrente ||
														formData?.contiCorrenti?.[index]?.contoCorrente
													}
													type="text"
												/>
											</Grid>
											<Grid item flexGrow={1}>
												<TextField
													fullWidth
													InputLabelProps={{ shrink: true }}
													// error={!!errors?.timeToExpiry}
													/*helperText={t(errors?.timeToExpiry, {
														ns: "corporate",
													})}*/
													onChange={(event) =>
														handleInputContiCorrentiChange(event, index)
													}
													id="senderAccount"
													name="senderAccount"
													label={t("form.senderAccount", { ns: "corporate" })}
													value={
														updateFormData?.contiCorrenti?.[index]
															?.senderAccount ??
														formData?.contiCorrenti?.[index]?.senderAccount
													}
													type="text"
													disabled
												/>
											</Grid>
											{/** 
											<Grid item flexGrow={1}>
												<AndroidSwitch
													label={"Abilitato"}
													name="enabled"
													field={"enabled"}
													handleChangeChildren={(
														event: ChangeEvent<HTMLInputElement>
													) => handleInputContiCorrentiChange(event, index)}
													isCheckedProps={cc.enabled}
												/>
											</Grid>
											*/}
										</Grid>
									</AccordionDetails>
								</Accordion>
							)
						})}
					</CustomTabPanel>
					<Grid
						item
						sx={{
							position: "fixed",
							width: "100%",
							bottom: "16px",
							backgroundColor: "#FFF",
							zIndex: 9,
						}}
					>
						{isSavingForm ? (
							<CircularProgress size="50px" />
						) : (
							<Button
								variant="contained"
								onClick={handleSaveForm}
								sx={{ margin: "16px 0" }}
							>
								{t("form.buttons.save", { ns: "corporate" })}
							</Button>
						)}
					</Grid>
				</Grid>
			)}
			{!!alertMessage && (
				<Snackbar
					sx={{ width: "calc(200% - 50px)" }}
					anchorOrigin={{ vertical: "top", horizontal: "left" }}
					open={isErrorSnackBarOpen}
					autoHideDuration={30000}
					onClose={() => setIsErrorSnackBarOpen(false)}
				>
					<Alert
						onClose={() => setIsErrorSnackBarOpen(false)}
						severity="error"
						variant="filled"
						sx={{ width: "200%" }}
					>
						{alertMessage}
					</Alert>
				</Snackbar>
			)}
		</>
	)
}

export default CreateForm
